import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../App';
import { useNavigate } from 'react-router-dom';
import AttorneyReviewQueuePanel from './AttorneyReviewQueuePanel';
import AttorneyAssistOrdersPanel from './AttorneyAssistOrdersPanel';
import { API_BASE } from '../lib/apiBase';

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [analytics, setAnalytics] = useState({});
  const [transactions, setTransactions] = useState([]);
  const [users, setUsers] = useState([]);

  const fetchAnalytics = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/analytics`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      const data = await response.json();
      setAnalytics(data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    }
  }, []);

  const fetchTransactions = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/transactions`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      const data = await response.json();
      setTransactions(data.transactions || []);
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/users`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      const data = await response.json();
      setUsers(data.users || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  }, []);

  useEffect(() => {
    if (user?.is_admin) {
      fetchAnalytics();
      fetchTransactions();
      fetchUsers();
    }
  }, [user?.is_admin, fetchAnalytics, fetchTransactions, fetchUsers]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Redirect if not admin - check after all hooks
  if (!user?.is_admin) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="bg-white rounded-xl p-8 shadow-lg text-center">
          <i className="fas fa-lock text-4xl text-red-500 mb-4"></i>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Access Denied</h2>
          <p className="text-slate-600">Admin access required.</p>
        </div>
      </div>
    );
  }

  const tabs = ['overview', 'transactions', 'users', 'review-queue', 'attorney-assist', 'templates'];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Admin Dashboard</h1>
              <p className="text-slate-600">Internal read visibility — not part of the public launch product.</p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <div className="text-sm text-slate-500">Administrator</div>
                <div className="font-semibold text-slate-900">{user?.full_name}</div>
              </div>
              <button
                onClick={handleLogout}
                className="px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors font-semibold"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-8">
        {/* Navigation Tabs */}
        <div className="flex flex-wrap gap-1 bg-slate-200 rounded-lg p-1 mb-8">
          {tabs.map((tab) => (
            <button
              key={tab}
              data-testid={`admin-tab-${tab}`}
              onClick={() => setActiveTab(tab)}
              className={`px-6 py-3 rounded-md font-semibold capitalize transition-all ${activeTab === tab
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
                }`}
            >
              {tab === 'review-queue'
                ? 'Review Queue'
                : tab === 'attorney-assist'
                  ? 'Attorney Assist'
                  : tab}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <OverviewTab analytics={analytics} />
        )}

        {activeTab === 'transactions' && (
          <TransactionsTab transactions={transactions} />
        )}

        {activeTab === 'users' && (
          <UsersTab users={users} />
        )}

        {activeTab === 'review-queue' && (
          <AttorneyReviewQueuePanel />
        )}

        {activeTab === 'attorney-assist' && (
          <AttorneyAssistOrdersPanel />
        )}

        {activeTab === 'templates' && (
          <TemplatesTab />
        )}
      </div>
    </div>
  );
};

// Overview Tab Component
const OverviewTab = ({ analytics }) => {
  const stats = [
    {
      title: 'Total Users',
      value: analytics.total_users || 0,
      icon: 'fas fa-users',
      color: 'blue'
    },
    {
      title: 'Total Transactions',
      value: analytics.total_transactions || 0,
      icon: 'fas fa-credit-card',
      color: 'green'
    },
    {
      title: 'Successful Payments',
      value: analytics.successful_payments || 0,
      icon: 'fas fa-check-circle',
      color: 'emerald'
    },
    {
      title: 'Conversion Rate',
      value: `${(analytics.conversion_rate || 0).toFixed(1)}%`,
      icon: 'fas fa-chart-line',
      color: 'purple'
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      blue: 'bg-blue-100 text-blue-600',
      green: 'bg-green-100 text-green-600',
      emerald: 'bg-emerald-100 text-emerald-600',
      purple: 'bg-purple-100 text-purple-600'
    };
    return colors[color] || 'bg-slate-100 text-slate-600';
  };

  return (
    <div className="space-y-8">
      {/* Stats Grid */}
      <div className="grid md:grid-cols-4 gap-6">
        {stats.map((stat) => (
          <div key={stat.title} className="bg-white rounded-xl p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-600 text-sm font-semibold">{stat.title}</p>
                <p className="text-3xl font-bold text-slate-900 mt-2">{stat.value}</p>
              </div>
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${getColorClasses(stat.color)}`}>
                <i className={`${stat.icon} text-xl`}></i>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Revenue by Package */}
      <div className="bg-white rounded-xl p-6 shadow-sm">
        <h3 className="text-xl font-bold text-slate-900 mb-6">Revenue by Package</h3>
        <div className="space-y-4">
          {(analytics.revenue_by_package || []).map((item) => (
            <div key={item._id} className="flex items-center justify-between p-4 border border-slate-200 rounded-lg">
              <div>
                <h4 className="font-semibold text-slate-900 capitalize">{item._id}</h4>
                <p className="text-slate-600 text-sm">{item.count} purchases</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-emerald-600">${item.total_revenue}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Transactions Tab Component
const TransactionsTab = ({ transactions }) => {
  return (
    <div className="bg-white rounded-xl shadow-sm">
      <div className="p-6 border-b border-slate-200">
        <h2 className="text-xl font-bold text-slate-900">Recent Transactions</h2>
        <p className="text-slate-600">Track all payment transactions</p>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Date</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">User</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Package</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Amount</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {transactions.length === 0 ? (
              <tr>
                <td colSpan="5" className="px-6 py-8 text-center text-slate-500">
                  No transactions yet
                </td>
              </tr>
            ) : (
              transactions.map((transaction) => (
                <tr key={transaction.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                    {new Date(transaction.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                    {transaction.user_email || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900 capitalize">
                    {transaction.package_id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-emerald-600">
                    ${transaction.amount}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${transaction.payment_status === 'paid'
                      ? 'bg-green-100 text-green-800'
                      : transaction.payment_status === 'pending'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-red-100 text-red-800'
                      }`}>
                      {transaction.payment_status}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Users Tab Component
const UsersTab = ({ users }) => {
  return (
    <div className="bg-white rounded-xl shadow-sm">
      <div className="p-6 border-b border-slate-200">
        <h2 className="text-xl font-bold text-slate-900">User Management</h2>
        <p className="text-slate-600">View and manage platform users</p>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Name</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Email</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Joined</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Role</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {users.length === 0 ? (
              <tr>
                <td colSpan="5" className="px-6 py-8 text-center text-slate-500">
                  No users yet
                </td>
              </tr>
            ) : (
              users.map((user_item) => (
                <tr key={user_item.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-slate-900">
                    {user_item.full_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                    {user_item.email}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                    {new Date(user_item.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${user_item.is_admin ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                      }`}>
                      {user_item.is_admin ? 'Admin' : 'User'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                    <button className="text-emerald-600 hover:text-emerald-700 font-semibold">
                      View Details
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Templates Tab Component
const TemplatesTab = () => {
  const [templates, setTemplates] = useState([
    {
      id: 'eb2-petition',
      name: 'EB-2 NIW Petition Letter',
      category: 'EB-2 NIW',
      lastModified: '2024-09-24'
    },
    {
      id: 'eb2-cover',
      name: 'EB-2 NIW Cover Letter',
      category: 'EB-2 NIW',
      lastModified: '2024-09-24'
    },
    {
      id: 'asylum-statement',
      name: 'Asylum Credibility Statement',
      category: 'Asylum',
      lastModified: '2024-09-24'
    }
  ]);

  return (
    <div className="space-y-6">
      {/* Add Template Button */}
      <div className="bg-white rounded-xl p-6 shadow-sm">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold text-slate-900">Document Templates</h2>
            <p className="text-slate-600">Manage petition and document templates</p>
          </div>
          <button
            className="bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2 rounded-lg font-semibold"
            data-testid="add-template-btn"
          >
            <i className="fas fa-plus mr-2"></i>
            Add Template
          </button>
        </div>
      </div>

      {/* Templates List */}
      <div className="bg-white rounded-xl shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Template Name</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Category</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Last Modified</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {templates.map((template) => (
                <tr key={template.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-slate-900">
                    {template.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                    {template.category}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                    {new Date(template.lastModified).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm space-x-3">
                    <button className="text-emerald-600 hover:text-emerald-700 font-semibold">
                      Edit
                    </button>
                    <button className="text-blue-600 hover:text-blue-700 font-semibold">
                      Preview
                    </button>
                    <button className="text-red-600 hover:text-red-700 font-semibold">
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;